// Reading a time somebody typed.
//
// A barn types "100" and means one o'clock in the afternoon. Making them pick
// an hour, then a minute, then AM or PM is three decisions for something they
// already know — so the picker takes text, and this works out what they meant.
//
// Everything it accepts:
//
//   1        3        11          bare hour
//   100      330      1130        no separator
//   1:00     3:30     11:30       with one
//   1300     0930                 24-hour, when the hour can't be 12-hour
//   13:00    09:30                same, with a separator
//   1p       1 pm     1:00PM      said outright
//   7a       7 am     7:00 am
//   noon     midnight             said in words
//
// The AM/PM guess only runs when the text doesn't say. The rule is the barn's
// working day, not the clock's:
//
//   1–6   afternoon    lessons, turnout, the second shift
//   7–11  morning      feed, turnout, the first shift
//   12    noon
//
// So "6" is six in the evening and "7" is seven in the morning, which is a
// discontinuity — and the right one, because 6pm lessons and 7am feed are both
// ordinary and 6am lessons and 7pm feed are not. Anyone who means the other one
// types "6a" or "7p", which is still fewer keystrokes than three dropdowns.
//
// A guess nobody can see is a guess nobody can correct, so the field it feeds
// echoes the result back as "1:00 PM" the moment it parses.

/** Hours that mean PM when the typist didn't say. */
const AFTERNOON = new Set([1, 2, 3, 4, 5, 6, 12]);

const WORDS = {
  noon: '12:00',
  midday: '12:00',
  midnight: '00:00',
};

/**
 * Text → "HH:MM" 24-hour, or null when it can't be read.
 *
 * Null means "I don't know", never a fallback: guessing at unreadable input is
 * how a lesson ends up at a time nobody chose.
 */
export function parseTimeInput(text) {
  if (text == null) return null;
  const raw = String(text).trim().toLowerCase();
  if (!raw) return null;

  if (WORDS[raw]) return WORDS[raw];

  // Pull the meridiem off the end first, so what's left is only digits.
  let meridiem = null;
  let body = raw;
  const mer = /\s*([ap])\.?m?\.?$/.exec(body);
  if (mer) {
    meridiem = mer[1] === 'a' ? 'am' : 'pm';
    body = body.slice(0, mer.index).trim();
  }

  body = body.replace(/[.\s]/g, '');
  if (!body) return null;

  let hour;
  let minute;

  const withSep = /^(\d{1,2}):(\d{1,2})$/.exec(body);
  if (withSep) {
    hour = Number(withSep[1]);
    minute = Number(withSep[2]);
  } else if (/^\d+$/.test(body)) {
    if (body.length <= 2) {
      hour = Number(body);
      minute = 0;
    } else if (body.length === 3) {
      hour = Number(body.slice(0, 1));
      minute = Number(body.slice(1));
    } else if (body.length === 4) {
      hour = Number(body.slice(0, 2));
      minute = Number(body.slice(2));
    } else {
      return null;
    }
  } else {
    return null;
  }

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (minute > 59 || minute < 0) return null;

  if (meridiem) {
    // Said outright: 12am is midnight, 12pm is noon, and anything above 12
    // with a meridiem is a contradiction rather than something to salvage.
    if (hour < 1 || hour > 12) return null;
    let h = hour % 12;
    if (meridiem === 'pm') h += 12;
    return stamp(h, minute);
  }

  // No meridiem. An hour above 12 can only be 24-hour, so it isn't a guess.
  if (hour > 23 || hour < 0) return null;
  if (hour === 0) return stamp(0, minute);
  if (hour > 12) return stamp(hour, minute);

  const h = hour % 12;
  return stamp(AFTERNOON.has(hour) ? h + 12 : h, minute);
}

function stamp(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "13:00" → "1:00 PM". The echo, so a guess is visible. */
export function formatTimeValue(hhmm) {
  if (!hhmm) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return '';
  const h24 = Number(m[1]);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${m[2]} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

/** Quarter-hour suggestions across a barn's day, for the browser's own autocomplete. */
export function timeSuggestions() {
  const out = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of ['00', '15', '30', '45']) out.push(formatTimeValue(`${String(h).padStart(2, '0')}:${m}`));
  }
  return out;
}
